import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
  SupportAdminSliceInitialState,
  APIResponse,
  SubmitHelpRequestRequestPayload,
  SubmitHelpRequestSuccessResponse,
  GetSupportTicketsRequestPayload,
  GetSupportTicketsSuccessResponse,
  SendReplyRequestPayload,
  SendReplySuccessResponse,
  MarkAsSpamRequestPayload,
  MarkAsSpamSuccessResponse,
  SupportTicket,
} from '@/interface';

const initialState: SupportAdminSliceInitialState = {
  isLoading: false,
  isSendingReply: false,
  isMarkingAsSpam: false,
  supportAdminSuccess: false,
  supportAdminFailure: false,
  supportTickets: {
    tickets: [],
    currentPage: 0,
    totalPages: 0,
    totalResults: 0,
    nextPage: null,
  },
};

const supportAdminSlice = createSlice({
  name: 'supportAdmin',
  initialState,
  reducers: {
    /**
     * submit help request
     */
    submitHelpRequestRequest: (state, action: PayloadAction<SubmitHelpRequestRequestPayload>) => {
      state.isLoading = true;
      state.supportAdminSuccess = false;
      state.supportAdminFailure = false;
    },
    submitHelpRequestSuccess: (state, action: PayloadAction<SubmitHelpRequestSuccessResponse>) => {
      state.isLoading = false;
      state.supportAdminSuccess = action.payload.status_code;
    },
    submitHelpRequestFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.supportAdminFailure = action.payload.status_code;
    },

    /**
     * reset messages
     */
    resetSupportAdminMessage: state => {
      state.supportAdminFailure = false;
      state.supportAdminSuccess = false;
    },

    cancelSupportAdminRequest: state => {
      state.isLoading = false;
      state.isSendingReply = false;
      state.isMarkingAsSpam = false;
    },
    /**
     * get support tickets request
     */
    getSupportTicketsRequest: (state, action: PayloadAction<GetSupportTicketsRequestPayload>) => {
      state.isLoading = true;
      state.supportAdminFailure = false;
      state.supportAdminSuccess = false;
    },
    getSupportTicketsSuccess: (state, action: PayloadAction<GetSupportTicketsSuccessResponse>) => {
      state.isLoading = false;
      if (!state.supportTickets) {
        state.supportTickets = {
          tickets: [],
          currentPage: 0,
          totalPages: 0,
          totalResults: 0,
          nextPage: null,
        };
      }
      state.supportTickets.tickets = action.payload.data.support_tickets;
      if (action.payload.data.support_tickets.length === 0) {
        state.supportAdminFailure = (action.payload.status_code === 'E-10054' || action.payload.status_code === 'E-10044') ? action.payload.status_code : 'E-10054';
        state.supportTickets.totalPages = 0;
        state.supportTickets.totalResults = 0;
        state.supportTickets.currentPage = 0;
        state.supportTickets.nextPage = null;
      } else {
        state.supportAdminSuccess = action.payload.status_code;
        state.supportTickets.totalPages = action.payload.data.total_pages;
        state.supportTickets.totalResults = action.payload.data.total_results;
        state.supportTickets.currentPage = action.payload.data.current_page;
        state.supportTickets.nextPage = action.payload.data.next_page;
      }
    },
    getSupportTicketsFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.supportAdminFailure = action.payload.status_code;
      if (action.payload.status_code === 'E-10054' || action.payload.status_code === 'E-10044') {
        if (!state.supportTickets) {
          state.supportTickets = {
            tickets: [],
            currentPage: 0,
            totalPages: 0,
            totalResults: 0,
            nextPage: null,
          };
        } else {
          state.supportTickets.tickets = [];
          state.supportTickets.totalPages = 0;
          state.supportTickets.totalResults = 0;
          state.supportTickets.currentPage = 0;
          state.supportTickets.nextPage = null;
        }
      }
    },

    /**
     * send reply request
     */
    sendReplyRequest: (state, action: PayloadAction<SendReplyRequestPayload>) => {
      state.isSendingReply = true;
      state.supportAdminSuccess = false;
      state.supportAdminFailure = false;
    },
    sendReplySuccess: (state, action: PayloadAction<SendReplySuccessResponse>) => {
      state.isSendingReply = false;
      state.supportAdminSuccess = action.payload.status_code;
      // Update the ticket in the list
      if (state.supportTickets?.tickets) {
        const ticketIndex = state.supportTickets.tickets.findIndex(
          t => t.id === action.payload.data.id,
        );
        if (ticketIndex !== -1) {
          state.supportTickets.tickets[ticketIndex] = {
            ...state.supportTickets.tickets[ticketIndex],
            admin_subject: action.payload.data.subject,
            admin_message: action.payload.data.message,
            admin_replied_date: action.payload.data.admin_replied_date,
            status: action.payload.data.status,
          };
        }
      }
    },
    sendReplyFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isSendingReply = false;
      state.supportAdminFailure = action.payload.status_code;
    },

    /**
     * mark as spam request
     */
    markAsSpamRequest: (state, action: PayloadAction<MarkAsSpamRequestPayload>) => {
      state.isMarkingAsSpam = true;
      state.supportAdminSuccess = false;
      state.supportAdminFailure = false;
    },
    markAsSpamSuccess: (state, action: PayloadAction<MarkAsSpamSuccessResponse>) => {
      state.isMarkingAsSpam = false;
      state.supportAdminSuccess = action.payload.status_code;
      // Update the ticket in the list
      if (state.supportTickets?.tickets) {
        const ticketIndex = state.supportTickets.tickets.findIndex(
          t => t.id === action.payload.data.id,
        );
        if (ticketIndex !== -1) {
          state.supportTickets.tickets[ticketIndex] = {
            ...state.supportTickets.tickets[ticketIndex],
            status: action.payload.data.status,
          };
        }
      }
    },
    markAsSpamFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isMarkingAsSpam = false;
      state.supportAdminFailure = action.payload.status_code;
    },
    
    addSupportTicketData: (state, action: PayloadAction<SupportTicket>) => {
      if (!state.supportTickets?.tickets) return;
    
      const exists = state.supportTickets.tickets.some(
        ticket => ticket.id === action.payload.id
      );
    
      if (!exists) {
        state.supportTickets.tickets.unshift(action.payload);
        state.supportTickets.totalResults =
          (state.supportTickets.totalResults || 0) + 1;
      }
    },
  },
});

export const {
  // submit help request
  submitHelpRequestRequest,
  submitHelpRequestSuccess,
  submitHelpRequestFailure,

  // reset messages
  resetSupportAdminMessage,
  cancelSupportAdminRequest,
  getSupportTicketsRequest,
  getSupportTicketsSuccess,
  getSupportTicketsFailure,
  sendReplyRequest,
  sendReplySuccess,
  sendReplyFailure,
  markAsSpamRequest,
  markAsSpamSuccess,
  markAsSpamFailure,
  addSupportTicketData,
} = supportAdminSlice.actions;
export default supportAdminSlice.reducer;

